import VueRouter from 'vue-router'
import { v4 as uuid } from 'uuid'
import useAttribution from '../src/composables/useAttribution'
import { useHistoryStore, useUserStore } from '@/stores'
import { $getDeviceId, $setDeviceId, $setToken } from '@/helpers'
import { isInThirdAppBrowser } from '@/utils/bowserEnv'
import { pageViewTrack, setPageViewParams } from '@/composables/tracks'
import { useAxonPageView } from '@/composables/axon/useAxonPageView'
import { ERROR_PAGE_LEVEL } from '@/constants'
import routes from '@/routes'
import './ployfills'

const router = new VueRouter({
  mode: 'history',
  base: '/',
  routes,
  scrollBehavior(_to, _from, _savedPosition) {
    return { x: 0, y: 0, behavior: 'smooth' }
  },
})

async function getUserInfo(to: any, next: any) {
  const userStore = useUserStore()
  try {
    if (!to.meta?.requiresUserInfo)
      userStore.init()
    else
      await userStore.init()
  }
  catch (error: any) {
    console.error(error)
    next({
      name: 'error',
      query: {
        level: ERROR_PAGE_LEVEL.DEFAULT,
        message: error.msg || error.message,
        redirect: window.encodeURIComponent(window.location.href),
      },
      replace: true,
    })
  }
}

// Router Guards
router.beforeEach(async (to, from, next) => {
  // Set document title
  document.title = to?.meta?.title
  const userStore = useUserStore()

  // 设置 prevPage, 用于埋点
  const historyStore = useHistoryStore()
  if (to.name !== from.name && from.meta?.title)
    historyStore.$setPrevPage(from.meta.title, window.location.origin, window.location.origin + from.fullPath)
  // 无须登陆
  if (to.meta?.requiresAuth === false) {
    next()
  }
  // 三方绑定
  else if (to.query && to.query.t) {
    $setToken(to.query.t as string)
    const query = { ...to.query }
    delete query.t
    await getUserInfo(to, next)
    next({
      ...to,
      name: to.name ?? undefined,
      query,
      replace: true,
    })
  }
  /**
   * 内部浏览器引流到外部浏览器
   * bt是原 token
   */
  else if (to.query && to.query.bt) {
    const query = { ...to.query }
    if (isInThirdAppBrowser()) {
      await getUserInfo(to, next)
      next()
      return
    }

    await userStore.transferLogin(to.query.bt as string)
    delete query.bt

    next({
      ...to,
      name: to.name ?? undefined,
      query,
      replace: true,
    })
  }
  // 首次登录
  else if (to.meta?.requiresAuth !== false && !userStore.isLogin()) {
    let deviceUuid = $getDeviceId()
    if (!deviceUuid) {
      deviceUuid = uuid()
      $setDeviceId(deviceUuid)
    }
    // 先收集归因信息, 再登录
    await useAttribution().init()
    await userStore.login(deviceUuid)
    await getUserInfo(to, next)
    next()
  }
  // 已登录
  else {
    await getUserInfo(to, next)
    next()
  }
})

// Clarity
router.afterEach((to) => {
  if (window.clarity) {
    window.clarity('event', to.name)
    window.clarity('set', 'route', to.name)
  }
})

// Axon page_view
router.afterEach(() => {
  const { reportPageView } = useAxonPageView()
  reportPageView()
})

// Store the route stack
router.afterEach((to, from) => {
  pageViewTrack(setPageViewParams(to.name!, to.path))

  const historyStore = useHistoryStore()

  if (to.name === 'history' && to.query.from === 'member')
    historyStore.$push(to.fullPath)

  // Note: same route || tabs route
  else if (to.name === from.name || ['history', 'home', 'member'].includes(to.name ?? ''))
    historyStore.$replace(to.fullPath)

  else
    historyStore.$push(to.fullPath)
})

export default router
